import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

type MediaUploadCtx = {
  hasUploads: boolean;
  begin: () => string;
  end: (id: string) => void;
};

const MediaUploadContext = createContext<MediaUploadCtx | null>(null);

export const MediaUploadProvider = ({ children }: { children: ReactNode }) => {
  const counter = useRef(0);
  const inFlight = useRef<Set<string>>(new Set());
  const [hasUploads, setHasUploads] = useState(false);

  const begin = useCallback(() => {
    counter.current += 1;
    const id = `upload-${counter.current}`;
    inFlight.current.add(id);
    setHasUploads(true);
    return id;
  }, []);

  const end = useCallback((id: string) => {
    inFlight.current.delete(id);
    if (inFlight.current.size === 0) {
      setHasUploads(false);
    }
  }, []);

  const value = useMemo(
    () => ({ hasUploads, begin, end }),
    [hasUploads, begin, end],
  );

  return (
    <MediaUploadContext.Provider value={value}>
      {children}
    </MediaUploadContext.Provider>
  );
};

export const useMediaUploadGate = (): boolean => {
  const ctx = useContext(MediaUploadContext);
  return ctx?.hasUploads ?? false;
};

export const useTrackMediaUpload = () => {
  const ctx = useContext(MediaUploadContext);
  return useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      if (!ctx) return fn();
      const id = ctx.begin();
      try {
        return await fn();
      } finally {
        ctx.end(id);
      }
    },
    [ctx],
  );
};
